import { ChevronsUp, Info, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { useTasks } from "../Hooks/useTasks";
import TaskCard from "./TaskCard";
import SiteTaskCard from "./SiteTaskCard";
import toast from "react-hot-toast";
import { extractErrorMessage } from "../../../Utils/extractErrorMessage";
import { SITE_TOAST_MESSAGES } from "./constants";

function extractAllTaskIds(tasks) {
  const ids = new Set();

  function traverse(taskList) {
    taskList.forEach((task) => {
      ids.add(task.title);
      if (task.sub_tasks && task.sub_tasks.length > 0) {
        traverse(task.sub_tasks);
      }
    });
  }

  traverse(tasks);
  return ids;
}

const ManageTasks = ({ onclose = null }) => {
  const [selectedTaskIds, setSelectedTaskIds] = useState(new Set());
  const [masterTasks, setMasterTasks] = useState([]);
  const [siteSearchQuery, setSiteSearchQuery] = useState("");

  const {
    loading,
    tasks,
    fetchTasksBySiteId,
    fetchMasterTask,
    removeTask,
    ManageTask,
    addTask,
    createFormMaster,
    removeTaskById,
    removeTasksBulk,
  } = useTasks({ pagination: false });
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMasterOpen, setIsMasterOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [newTask, setNewTask] = useState({
    title: "",
    description: "",
    weight: "",
  });

  useEffect(() => {
      fetchTasksBySiteId();
  }, []);

  const addCustomTask = async () => {
    if (newTask.title.trim() === "") return;
    const data = await addTask({
      ...newTask,
      super_task: isModalOpen === "root" ? null : isModalOpen,
      order: selectedTasks.length + 1,
    });
    await fetchTasksBySiteId();
    setNewTask({ title: "", description: "", weight: "" });
    setIsModalOpen(false);
  };

  const handleDelete = async (taskId) => {
    const data = await removeTaskById(taskId);
  };

  useEffect(() => {
    async function fetchData() {
      const data = await fetchMasterTask();
      setMasterTasks(data || []);
    }
    fetchData();
  }, []);

  const areAllChildrenSelected = (task) => {
    if (!task.sub_tasks || task.sub_tasks.length === 0) return false;
    return task.sub_tasks.every((sub) => {
      return selectedTaskIds.has(sub.title) || areAllChildrenSelected(sub);
    });
  };

  const areSomeChildrenSelected = (task) => {
    if (!task.sub_tasks || task.sub_tasks.length === 0) return false;
    return task.sub_tasks.some((sub) => {
      return selectedTaskIds.has(sub.title) || areSomeChildrenSelected(sub);
    });
  };

  const getCheckState = useMemo(() => {
    return (task) => {
      const allChildren = areAllChildrenSelected(task);
      const someChildren = areSomeChildrenSelected(task);

      const isChecked = selectedTaskIds.has(task.title) || allChildren;
      const isIndeterminate = !isChecked && someChildren;

      return { isChecked, isIndeterminate };
    };
  }, [selectedTaskIds]);

  const removeTaskFromSiteByTitle = async (title) => {
    const res = await removeTask(title);
    if (res === 200 || res != null) {
      setSelectedTasks(selectedTasks.filter((task) => task.title !== title));
    }
  };

  const toggleTask = async (task) => {
    const updated = new Set(selectedTaskIds);
    const isChecked = updated.has(task.title);

    // collect all children (recursive)
    const collectChildren = (t) => {
      let ids = [t.title];
      if (t.sub_tasks && t.sub_tasks.length > 0) {
        t.sub_tasks.forEach((sub) => {
          ids = ids.concat(collectChildren(sub));
        });
      }
      return ids;
    };

    // collect all ancestors (recursive search)
    const collectAncestors = (taskId, tasks, chain = []) => {
      for (let t of tasks) {
        if (t.title === taskId) return chain;
        if (t.sub_tasks?.length) {
          const res = collectAncestors(taskId, t.sub_tasks, [
            ...chain,
            t.title,
          ]);
          if (res.length) return res;
        }
      }
      return [];
    };

    // helper to find task by title
    const findTaskByTitle = (tasks, title) => {
      for (let t of tasks) {
        if (t.title === title) return t;
        if (t.sub_tasks?.length) {
          const found = findTaskByTitle(t.sub_tasks, title);
          if (found) return found;
        }
      }
      return null;
    };

    const allChildren = collectChildren(task);
    const ancestors = collectAncestors(task.title, masterTasks);

    if (!isChecked) {
      try {
        const res = await createFormMaster(task.id);
        if (res.status === 201) {
          // ✅ add task + children + ancestors
          [...allChildren, ...ancestors].forEach((id) => updated.add(id));
          await fetchTasksBySiteId();
          setSelectedTaskIds(new Set(updated));
        }
      } catch (err) {
        const msg =
          extractErrorMessage(err) || SITE_TOAST_MESSAGES.TASK_ADDITION_FAILURE;
        toast.error(msg);
      }
    } else {
      // ✅ remove task + children (but keep ancestors if siblings still selected)
      allChildren.forEach((id) => updated.delete(id));

      // check each ancestor: only remove if *none* of its children remain
      ancestors.forEach((ancestorId) => {
        const ancestorTask = findTaskByTitle(masterTasks, ancestorId);
        const stillHasSelectedChild = ancestorTask?.sub_tasks?.some((sub) =>
          updated.has(sub.title)
        );
        if (!stillHasSelectedChild) {
          updated.delete(ancestorId);
        }
      });

      setSelectedTaskIds(new Set(updated));
      await removeTaskFromSiteByTitle(task.title);
    }
  };

  // Collect all task IDs recursively (main + subtasks)
  const getAllTaskIds = (tasks) => {
    let ids = [];
    tasks.forEach((task) => {
      ids.push(task.title);
      if (task.sub_tasks?.length > 0) {
        ids = ids.concat(getAllTaskIds(task.sub_tasks));
      }
    });
    return ids;
  };

  const handleSelectAll = async () => {
    const allIds = getAllTaskIds(masterTasks);
    const isAllSelected = allIds.every((id) => selectedTaskIds.has(id)); // check if all ids are selected
    const updated = new Set(selectedTaskIds);

    if (isAllSelected) {
      try {
        const res = await removeTasksBulk(allIds);
        if (res) {
          allIds.forEach((id) => updated.delete(id));
          setSelectedTaskIds(updated);
        }
      } catch (err) {
        const msg =
          extractErrorMessage(err) || SITE_TOAST_MESSAGES.TASK_DELETION_FAILURE;
        toast.error(msg);
      }
    } else {
      try {
        const res = await createFormMaster(masterTasks.map((t) => t.id));
        if (res?.status === 201) {
          allIds.forEach((id) => updated.add(id));
          setSelectedTaskIds(updated);
          await fetchTasksBySiteId();
        }
      } catch (err) {
        const msg =
          extractErrorMessage(err) || SITE_TOAST_MESSAGES.TASK_ADDITION_FAILURE;
        toast.error(msg);
      }
    }
  };

  const reorderList = (list, source, destination) => {
    const updated = [...list];
    const [moved] = updated.splice(source.index, 1);
    updated.splice(destination.index, 0, moved);
    return updated;
  };

  const onDragEnd = (result) => {
    const { source, destination } = result;
    if (!destination) return;

    if (source.droppableId === destination.droppableId) {
      if (source.droppableId === "root") {
        setSelectedTasks((prev) => {
          const updated = reorderList(prev, source, destination);

          ManageTask(updated).catch(() => {
            toast.error("Failed to update order");
          });

          return updated;
        });
      } else {
        setSelectedTasks((prev) => {
          const updated = updateNestedTasks(
            prev,
            destination.droppableId,
            source,
            destination
          );
          return updated;
        });
      }
    }
  };

  const updateNestedTasks = (tasks, droppableId, source, destination) => {
    return tasks.map((task) => {
      if (task.id.toString() === droppableId) {
        const updatedSub = reorderList(
          task.sub_tasks || [],
          source,
          destination
        );
        ManageTask(updatedSub).catch(() => {
          toast.error("Failed to update order");
        });
        return { ...task, sub_tasks: updatedSub };
      }

      if (task.sub_tasks?.length) {
        return {
          ...task,
          sub_tasks: updateNestedTasks(
            task.sub_tasks,
            droppableId,
            source,
            destination
          ),
        };
      }

      return task;
    });
  };
  useEffect(() => {
    if (tasks) {
      setSelectedTasks(tasks);
      setSelectedTaskIds(extractAllTaskIds(tasks));
    }
  }, [tasks]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white p-4 rounded-lg w-11/12 md:w-[90%]">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Manage Tasks</h2>
          <button
            onClick={() => onclose()}
            className="text-gray-500 hover:text-gray-700"
          >
            <X />
          </button>
        </div>
        <div className="flex items-center gap-2 mb-4 text-sm text-gray-600 bg-blue-50 p-2 rounded-md">
          <Info className="w-20 h-20 md:h-4 md:w-4 text-blue-500" />
          <p>
            Select tasks from the master list using checkboxes, or click{" "}
            <strong>+ Add Custom Task</strong> to create custom task. Deselect
            from master list to delete the task.
          </p>
        </div>
        <div className="md:hidden">
          <button
            onClick={() => setIsMasterOpen(!isMasterOpen)}
            className="mb-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            <ChevronsUp
              className={`transform transition-transform duration-300 ${
                isMasterOpen ? "-rotate-90" : "rotate-90"
              }`}
            />
          </button>
        </div>

        <div className="relative overflow-x-hidden">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:min-w-[100%] ">
            <div className="px-2 rounded hidden md:block">
              <div className="flex justify-between items-center mb-2">
                <h3 className="font-semibold">Master Tasks</h3>
                <button
                  type="button"
                  onClick={handleSelectAll}
                  className={`text-xs px-3 py-1.5 rounded-md font-semibold border transition-all duration-75
                  ${
                    masterTasks.length > 0 &&
                    getAllTaskIds(masterTasks).every((id) => selectedTaskIds.has(id))
                      ? "bg-rose-400 text-gray-800 border-red-400 hover:bg-red-500"
                      : "bg-green-400 text-black border-green-400 hover:bg-green-500"
                  }`}
                >
                  {masterTasks.length > 0 &&
                  getAllTaskIds(masterTasks).every((id) =>
                    selectedTaskIds.has(id)
                  )
                    ? "Deselect All"
                    : "Select All"}
                </button>
              </div>

              <input
                type="text"
                placeholder="Search tasks..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full mb-3 px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />

              <div className="min-h-[50vh] max-h-[60vh] overflow-y-auto">
                <TaskCard
                  masterTasks={masterTasks.filter(
                    (task) =>
                      task.title
                        .toLowerCase()
                        .includes(searchQuery.toLowerCase()) // ✅ Only main tasks
                  )}
                  selectedTaskIds={selectedTaskIds}
                  setSelectedTaskIds={setSelectedTaskIds}
                  getCheckState={getCheckState}
                  toggleTask={toggleTask}
                />
              </div>
            </div>
            <div className="px-2 rounded">
              <h3 className="font-semibold mb-2">Site Tasks</h3>

              {/* Search Input */}
              <input
                type="text"
                placeholder="Search site tasks..."
                value={siteSearchQuery}
                onChange={(e) => setSiteSearchQuery(e.target.value)}
                className="w-full mb-3 px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />

              <div className="min-h-[50vh] max-h-[60vh] overflow-y-auto pl-5">
                <DragDropContext onDragEnd={onDragEnd}>
                  <Droppable droppableId="root" type="TASK">
                    {(provided) => (
                      <SiteTaskCard
                        selectedTasks={selectedTasks.filter(
                          (task) =>
                            task.title
                              .toLowerCase()
                              .includes(siteSearchQuery.toLowerCase()) // ✅ only filter main tasks
                        )}
                        setSelectedTasks={setSelectedTasks}
                        setIsModalOpen={setIsModalOpen}
                        provided={provided}
                        onDragEnd={onDragEnd}
                        handleDelete={handleDelete}
                      />
                    )}
                  </Droppable>
                </DragDropContext>
              </div>
            </div>
          </div>

          <div
            className={`md:hidden top-0 left-0 absolute transition-all ${
              isMasterOpen ? "left-0" : "-left-full"
            } duration-300 w-full h-full`}
          >
            <div
              className={`absolute top-0 left-0 bg-black z-20 transition-all duration-300 w-full h-full ${
                isMasterOpen ? "bg-opacity-30" : "bg-opacity-0"
              }`}
              onClick={() => setIsMasterOpen(false)}
            ></div>
            <div
              className={`px-2 rounded absolute top-0  shadow-lg bg-white w-[80%] h-full transition-all duration-300 md:hidden z-50`}
            >
              <div className="flex justify-between items-center mb-2">
                <h3 className="font-semibold">Master Tasks</h3>
                <button
                  type="button"
                  onClick={handleSelectAll}
                  className={`text-xs px-2 py-1 rounded-md font-medium border transition-all duration-75
                  ${
                    masterTasks.length > 0 &&
                    getAllTaskIds(masterTasks).every((id) => selectedTaskIds.has(id))
                      ? "bg-rose-400 text-gray-800 border-red-400 hover:bg-red-500"
                      : "bg-green-400 text-black border-green-400 hover:bg-green-500"
                  }`}
                >
                  {masterTasks.length > 0 &&
                  getAllTaskIds(masterTasks).every((id) =>
                    selectedTaskIds.has(id)
                  )
                    ? "Deselect All"
                    : "Select All"}
                </button>
              </div>
              <div className=" min-h-[50vh] max-h-[60vh] overflow-y-auto">
                <TaskCard
                  masterTasks={masterTasks}
                  selectedTaskIds={selectedTaskIds}
                  setSelectedTaskIds={setSelectedTaskIds}
                  getCheckState={getCheckState}
                  toggleTask={toggleTask}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      {isModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white rounded-lg shadow-lg p-4 w-96">
            <h2 className="text-lg font-semibold mb-2">Add Custom Task</h2>
            <div className="flex items-center gap-2 mb-4 text-sm text-gray-600 bg-blue-50 p-2 rounded-md">
              <Info className="w-15 h-15 text-blue-500" />
              <p>Higher weight = more impact on site/project progress.</p>
            </div>
            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">
                Title <sub className="text-red-500 top-0">*</sub>
              </label>
              <input
                type="text"
                value={newTask.title}
                onChange={(e) =>
                  setNewTask({ ...newTask, title: e.target.value })
                }
                className="w-full border rounded p-2"
                placeholder="Enter task title"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                Description
              </label>
              <textarea
                value={newTask.description}
                onChange={(e) =>
                  setNewTask({ ...newTask, description: e.target.value })
                }
                className="w-full border rounded p-2"
                placeholder="Enter task description"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">
                Weight <sub className="text-red-500 top-0">*</sub>
              </label>
              <input
                type="number"
                value={newTask.weight}
                onChange={(e) =>
                  setNewTask({ ...newTask, weight: e.target.value })
                }
                className="w-full border rounded p-2"
                placeholder="Enter task weight"
                required
                min={1}
              />
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setNewTask({ title: "", description: "", weight: "" });
                  setIsModalOpen(false);
                }}
                className="px-4 py-1 bg-gray-300 rounded hover:bg-gray-400"
              >
                Cancel
              </button>
              <button
                onClick={addCustomTask}
                disabled={!newTask.weight || Number(newTask.weight) <= 0}
                className={`px-4 py-1 rounded text-white transition ${
                  !newTask.weight || Number(newTask.weight) <= 0
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-blue-600 hover:bg-blue-700"
                }`}
              >
                Add Task
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageTasks;
